import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { HiOutlinePhotograph, HiOutlineEmojiSad, HiOutlineCollection, HiOutlineCheckCircle } from 'react-icons/hi';
import { MdNavigateBefore } from 'react-icons/md';
import ItemService from '../service/ItemService';
import ShopService from '../service/ShopService';
import '../css/admin.css'
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import ImageCropper from '../components/ImageCropper';
import { PRODUCT_PHOTO, checkPhotoFile } from '../util/imageFile';

// Products live in sub-categories; a top-level category without children can hold products itself.
const categoryGroups = (list) => (list || []).map(parent => ({
    label: parent.categoryName,
    options: parent.subCategories && parent.subCategories.length > 0
        ? parent.subCategories.map(sub => ({ id: sub.categoryId, name: sub.categoryName }))
        : [{ id: parent.categoryId, name: parent.categoryName }]
}))

const CreateItemShop = () => {
    const history = useHistory();
    const location = useLocation();

    const [categories, setCategories] = useState(null)
    const [loadError, setLoadError] = useState(false)
    const [categoryId, setCategoryId] = useState(location.state?.categoryId ? String(location.state.categoryId) : '')
    const [name, setName] = useState('')
    const [file, setFile] = useState(null)
    const [preview, setPreview] = useState(null)
    const [original, setOriginal] = useState(null)
    const [cropping, setCropping] = useState(false)
    const [errors, setErrors] = useState({})
    const [submitError, setSubmitError] = useState('')
    const [isSaving, setIsSaving] = useState(false)

    const loadCategories = () => {
        setLoadError(false)
        ItemService.getShopCategories().then(res => {
            if (res.status) setCategories(res.data.data || [])
        }).catch(error => {
            console.log(error)
            if (error?.response?.status === 401) {
                history.push('/login')
                return
            }
            setLoadError(true)
        });
    }

    useEffect(() => {
        loadCategories()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Release the object URL when the preview changes.
    useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

    // picking a photo opens the cropper; the cropped 3:4 JPEG is what gets uploaded
    const onFileChange = (event) => {
        const picked = event.target.files[0]
        event.target.value = null
        if (!picked) return
        const problem = checkPhotoFile(picked)
        if (problem) {
            setErrors(e => ({ ...e, file: problem }))
        } else {
            setErrors(e => ({ ...e, file: undefined }))
            setOriginal(picked)
            setCropping(true)
        }
    }

    const onCropped = (results) => {
        setCropping(false)
        setFile(results.photo.file)
        setPreview(results.photo.url)
    }

    const onCropCancel = (message) => {
        setCropping(false)
        if (message) {
            setErrors(e => ({ ...e, file: message }))
            if (!file) setOriginal(null)
        }
    }

    const onSubmit = (event) => {
        event.preventDefault()
        const found = {}
        if (!file) found.file = 'Please add a product photo.'
        if (name.trim().length < 2) found.name = 'Please enter a product name (at least 2 characters).'
        if (!categoryId) found.category = 'Please choose a category.'
        setErrors(found)
        setSubmitError('')
        if (Object.keys(found).length > 0) return

        setIsSaving(true)
        const formData = new FormData();
        formData.append("file", file);
        formData.append("itemName", name.trim());
        formData.append("categoryId", categoryId);

        ShopService.addNewShopItem(formData).then(res => {
            setIsSaving(false)
            if (res.status === 200) {
                history.push({
                    pathname: '/shop/items/list/' + categoryId,
                    state: { created: name.trim() }
                })
            }
        }).catch(error => {
            setIsSaving(false)
            setSubmitError('Could not create the product. Please try again.')
        });
    }

    const groups = categoryGroups(categories)

    return (
        <main className='mv-page mv-create'>
            <Link to='/shop/inventory' className='mv-back-link'><MdNavigateBefore size={20} /> Inventory</Link>
            <div className='mv-page-head'>
                <div>
                    <h1 className='mv-list-title'>Add product</h1>
                    <p className='mv-page-sub'>Start with a photo, a name and a category. You can add sizes and prices next.</p>
                </div>
            </div>

            {loadError &&
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load categories'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={loadCategories} />}

            {!loadError && categories == null && <Skeleton height={360} radius='14px' />}

            {!loadError && categories != null && categories.length === 0 &&
                <EmptyState icon={<HiOutlineCollection />} title='Create a category first'
                    text='Products belong to a category. Add one in Inventory, then come back here.'
                    actionLabel='Go to inventory' onAction={() => history.push('/shop/inventory')} />}

            {!loadError && categories != null && categories.length > 0 &&
                <form className='mv-card mv-create-form' onSubmit={onSubmit} noValidate>
                    <div className='mv-create-grid'>
                        <div className='mv-field'>
                            <span className='mv-label' aria-hidden='true'>Main photo</span>
                            <label className='mv-upload mv-upload-lg' htmlFor='create-photo'>
                                <span className='mv-visually-hidden'>Main photo</span>
                                {preview ? <img src={preview} alt='Product preview' /> :
                                    <span className='mv-upload-empty'><HiOutlinePhotograph /> Add photo</span>}
                                <input id='create-photo' type='file' accept='image/jpeg,image/png,image/webp' onChange={onFileChange}
                                    aria-invalid={errors.file ? 'true' : undefined} />
                                {preview && <span className='mv-upload-change'>Change</span>}
                            </label>
                            {preview && original &&
                                <button type='button' className='mv-link-btn mv-recrop' onClick={() => setCropping(true)}>Crop again</button>}
                            {errors.file ? <span className='mv-field-error'>{errors.file}</span>
                                : <span className='mv-field-hint'>You'll crop it to 3:4 portrait, the shape used across the shop. A clear photo on a plain background works best.</span>}
                        </div>

                        <div className='mv-form'>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='create-name'>Product name</label>
                                <input id='create-name' className='mv-input' placeholder='e.g. Baby Girl frock flower print'
                                    value={name} onChange={e => setName(e.target.value)} autoFocus
                                    aria-invalid={errors.name ? 'true' : undefined} />
                                {errors.name && <span className='mv-field-error'>{errors.name}</span>}
                            </div>

                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='create-category'>Category</label>
                                <select id='create-category' className='mv-select' value={categoryId} onChange={e => setCategoryId(e.target.value)}
                                    aria-invalid={errors.category ? 'true' : undefined}>
                                    <option value=''>Choose a category…</option>
                                    {groups.map(group =>
                                        <optgroup key={group.label} label={group.label}>
                                            {group.options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                        </optgroup>
                                    )}
                                </select>
                                {errors.category && <span className='mv-field-error'>{errors.category}</span>}
                            </div>

                            <ol className='mv-create-steps'>
                                <li className='done'><HiOutlineCheckCircle /> Photo, name and category</li>
                                <li>Open the product to add sizes, prices and stock</li>
                                <li>Switch it on sale when it is ready</li>
                            </ol>
                        </div>
                    </div>

                    {submitError && <div className='mv-alert mv-alert-error' role='alert'>{submitError}</div>}

                    <div className='mv-form-actions'>
                        <button type='submit' className='mv-btn mv-btn-primary' disabled={isSaving}>
                            {isSaving ? 'Creating…' : 'Create product'}
                        </button>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={() => history.push('/shop/inventory')} disabled={isSaving}>
                            Cancel
                        </button>
                    </div>
                </form>}

            {cropping && original &&
                <ImageCropper file={original} steps={[PRODUCT_PHOTO]} title='Crop product photo'
                    onDone={onCropped} onCancel={onCropCancel} />}
        </main>
    );
};

export default CreateItemShop;
